import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, get, set, push } from "firebase/database";
import "./ValueBasedOffers.css";

/**
 * Value-Based Offers
 * ------------------
 * Lean data structure (also used as JSON export shape):
 *
 * {
 *   "id": "<rtdb push id>",
 *   "title": "Offer Set Name",
 *   "description": "Optional context / intro",
 *   "currency": "EUR",
 *   "createdAt": 0,
 *   "updatedAt": 0,
 *   "options": [
 *     {
 *       "id": "opt_xxx",
 *       "title": "Option title",
 *       "price": 0,
 *       "chapters": [
 *         {
 *           "id": "chp_xxx",
 *           "title": "Chapter title",
 *           "modules": [
 *             {
 *               "id": "mod_xxx",
 *               "title": "Module title",
 *               "text": "Optional paragraph",
 *               "bullets": ["bullet a", "bullet b"]
 *             }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * Saved at: /World/<userId>/valueBasedOffers/<setId>
 * Max options per set: 3
 */

const MAX_OPTIONS = 3;

const uid = (prefix) =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const emptyModule = () => ({
  id: uid("mod"),
  title: "",
  text: "",
  bullets: [],
});

const emptyChapter = () => ({
  id: uid("chp"),
  title: "",
  modules: [emptyModule()],
});

const emptyOption = (index = 0) => ({
  id: uid("opt"),
  title: `Option ${index + 1}`,
  price: 0,
  // Optional one-off setup fee on top of the monthly price. 0 = none.
  setupFee: 0,
  chapters: [emptyChapter()],
});

const emptySet = () => ({
  title: "",
  description: "",
  currency: "EUR",
  options: [emptyOption(0)],
});

// Sanitize for Firebase (no undefined values; arrays/objects only)
const sanitize = (val) => {
  if (Array.isArray(val)) return val.map(sanitize);
  if (val && typeof val === "object") {
    const out = {};
    for (const k of Object.keys(val)) {
      const v = sanitize(val[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return val === undefined ? null : val;
};

// Re-hydrate a set loaded from RTDB so chapters/modules/bullets are arrays
const hydrate = (raw) => {
  const opts = Array.isArray(raw?.options) ? raw.options : [];
  return {
    title: raw?.title || "",
    description: raw?.description || "",
    currency: raw?.currency || "EUR",
    options: opts.map((opt, i) => ({
      id: opt?.id || uid("opt"),
      title: opt?.title || `Option ${i + 1}`,
      price: typeof opt?.price === "number" ? opt.price : Number(opt?.price) || 0,
      setupFee:
        typeof opt?.setupFee === "number"
          ? opt.setupFee
          : Number(opt?.setupFee) || 0,
      chapters: (Array.isArray(opt?.chapters) ? opt.chapters : []).map((chp) => ({
        id: chp?.id || uid("chp"),
        title: chp?.title || "",
        modules: (Array.isArray(chp?.modules) ? chp.modules : []).map((mod) => ({
          id: mod?.id || uid("mod"),
          title: mod?.title || "",
          text: mod?.text || "",
          bullets: Array.isArray(mod?.bullets) ? mod.bullets : [],
        })),
      })),
    })),
  };
};

const ValueBasedOffers = ({ userId }) => {
  const [sets, setSets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptySet());
  const [activeOptionIdx, setActiveOptionIdx] = useState(0);
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Load
  useEffect(() => {
    const loadSets = async () => {
      if (!userId) return;
      try {
        const snap = await get(ref(database, `/World/${userId}/valueBasedOffers`));
        if (snap.exists()) {
          const list = [];
          snap.forEach((child) => {
            list.push({ id: child.key, ...child.val() });
          });
          list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setSets(list);
        }
      } catch (e) {
        console.error("Error loading value-based offers:", e);
        showNotification("Error loading value-based offers", "error");
      } finally {
        setIsLoading(false);
      }
    };
    loadSets();
  }, [userId]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: "", type: "" }), 3000);
  };

  // ---------- Draft mutation helpers ----------
  const updateOption = (optIdx, patch) => {
    setDraft((d) => {
      const options = d.options.map((o, i) => (i === optIdx ? { ...o, ...patch } : o));
      return { ...d, options };
    });
  };

  const updateChapter = (optIdx, chpIdx, patch) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => (j === chpIdx ? { ...c, ...patch } : c));
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const updateModule = (optIdx, chpIdx, modIdx, patch) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => {
          if (j !== chpIdx) return c;
          const modules = c.modules.map((m, k) => (k === modIdx ? { ...m, ...patch } : m));
          return { ...c, modules };
        });
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const addOption = () => {
    setDraft((d) => {
      if (d.options.length >= MAX_OPTIONS) return d;
      const next = [...d.options, emptyOption(d.options.length)];
      setActiveOptionIdx(next.length - 1);
      return { ...d, options: next };
    });
  };

  const removeOption = (optIdx) => {
    setDraft((d) => {
      if (d.options.length <= 1) return d;
      const options = d.options.filter((_, i) => i !== optIdx);
      return { ...d, options };
    });
    setActiveOptionIdx((i) => Math.max(0, i - (i >= optIdx ? 1 : 0)));
  };

  const addChapter = (optIdx) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) =>
        i === optIdx ? { ...opt, chapters: [...opt.chapters, emptyChapter()] } : opt
      );
      return { ...d, options };
    });
  };

  const removeChapter = (optIdx, chpIdx) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.filter((_, j) => j !== chpIdx);
        return { ...opt, chapters: chapters.length ? chapters : [emptyChapter()] };
      });
      return { ...d, options };
    });
  };

  const moveChapter = (optIdx, chpIdx, dir) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = [...opt.chapters];
        const target = chpIdx + dir;
        if (target < 0 || target >= chapters.length) return opt;
        [chapters[chpIdx], chapters[target]] = [chapters[target], chapters[chpIdx]];
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const addModule = (optIdx, chpIdx) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) =>
          j === chpIdx ? { ...c, modules: [...c.modules, emptyModule()] } : c
        );
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const removeModule = (optIdx, chpIdx, modIdx) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => {
          if (j !== chpIdx) return c;
          const modules = c.modules.filter((_, k) => k !== modIdx);
          return { ...c, modules: modules.length ? modules : [emptyModule()] };
        });
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const moveModule = (optIdx, chpIdx, modIdx, dir) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => {
          if (j !== chpIdx) return c;
          const modules = [...c.modules];
          const target = modIdx + dir;
          if (target < 0 || target >= modules.length) return c;
          [modules[modIdx], modules[target]] = [modules[target], modules[modIdx]];
          return { ...c, modules };
        });
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const updateBullet = (optIdx, chpIdx, modIdx, bIdx, value) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => {
          if (j !== chpIdx) return c;
          const modules = c.modules.map((m, k) => {
            if (k !== modIdx) return m;
            const bullets = [...m.bullets];
            bullets[bIdx] = value;
            return { ...m, bullets };
          });
          return { ...c, modules };
        });
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const addBullet = (optIdx, chpIdx, modIdx) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => {
          if (j !== chpIdx) return c;
          const modules = c.modules.map((m, k) =>
            k === modIdx ? { ...m, bullets: [...m.bullets, ""] } : m
          );
          return { ...c, modules };
        });
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  const removeBullet = (optIdx, chpIdx, modIdx, bIdx) => {
    setDraft((d) => {
      const options = d.options.map((opt, i) => {
        if (i !== optIdx) return opt;
        const chapters = opt.chapters.map((c, j) => {
          if (j !== chpIdx) return c;
          const modules = c.modules.map((m, k) => {
            if (k !== modIdx) return m;
            return { ...m, bullets: m.bullets.filter((_, b) => b !== bIdx) };
          });
          return { ...c, modules };
        });
        return { ...opt, chapters };
      });
      return { ...d, options };
    });
  };

  // ---------- Persistence ----------
  const handleNew = () => {
    setDraft(emptySet());
    setEditingId(null);
    setActiveOptionIdx(0);
  };

  const handleEdit = (set) => {
    setDraft(hydrate(set));
    setEditingId(set.id);
    setActiveOptionIdx(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this value-based offer set?")) return;
    try {
      await set(ref(database, `/World/${userId}/valueBasedOffers/${id}`), null);
      setSets((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) handleNew();
      showNotification("Deleted", "success");
    } catch (e) {
      console.error(e);
      showNotification("Error deleting set", "error");
    }
  };

  const handleSave = async () => {
    if (!userId) return;
    if (!draft.title.trim()) {
      showNotification("Set title is required", "error");
      return;
    }
    if (!draft.options.length) {
      showNotification("At least one option is required", "error");
      return;
    }

    const payload = sanitize({
      title: draft.title.trim(),
      description: draft.description?.trim() || "",
      currency: draft.currency || "EUR",
      options: draft.options.slice(0, MAX_OPTIONS).map((o) => ({
        id: o.id,
        title: o.title?.trim() || "",
        price: Number(o.price) || 0,
        setupFee: Math.max(0, Number(o.setupFee) || 0),
        chapters: (o.chapters || []).map((c) => ({
          id: c.id,
          title: c.title?.trim() || "",
          modules: (c.modules || []).map((m) => ({
            id: m.id,
            title: m.title?.trim() || "",
            text: m.text || "",
            bullets: (m.bullets || []).map((b) => (b || "").trim()).filter(Boolean),
          })),
        })),
      })),
      updatedAt: Date.now(),
    });

    try {
      if (editingId) {
        const r = ref(database, `/World/${userId}/valueBasedOffers/${editingId}`);
        const existing = await get(r);
        const createdAt = existing.exists()
          ? existing.val()?.createdAt || Date.now()
          : Date.now();
        await set(r, { ...payload, createdAt });
        setSets((prev) =>
          prev.map((s) =>
            s.id === editingId ? { id: editingId, createdAt, ...payload } : s
          )
        );
        showNotification("Saved", "success");
      } else {
        const listRef = ref(database, `/World/${userId}/valueBasedOffers`);
        const newRef = push(listRef);
        const createdAt = Date.now();
        await set(newRef, { ...payload, createdAt });
        const newSet = { id: newRef.key, createdAt, ...payload };
        setSets((prev) => [newSet, ...prev]);
        setEditingId(newRef.key);
        showNotification("Saved", "success");
      }
    } catch (e) {
      console.error(e);
      showNotification("Error saving", "error");
    }
  };

  // ---------- JSON export ----------
  const downloadJson = (record) => {
    const exportObj = {
      id: record.id,
      title: record.title,
      description: record.description || "",
      currency: record.currency || "EUR",
      createdAt: record.createdAt || null,
      updatedAt: record.updatedAt || null,
      options: (record.options || []).map((o) => ({
        id: o.id,
        title: o.title || "",
        price: Number(o.price) || 0,
        setupFee: Math.max(0, Number(o.setupFee) || 0),
        chapters: (o.chapters || []).map((c) => ({
          id: c.id,
          title: c.title || "",
          modules: (c.modules || []).map((m) => ({
            id: m.id,
            title: m.title || "",
            text: m.text || "",
            bullets: Array.isArray(m.bullets) ? m.bullets : [],
          })),
        })),
      })),
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (record.title || "value-based-offer")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    a.href = url;
    a.download = `${safeName || "value-based-offer"}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---------- Render ----------
  if (isLoading) {
    return (
      <div className="vbo">
        <div className="vbo__loading">Loading value-based offers...</div>
      </div>
    );
  }

  const activeOption = draft.options[activeOptionIdx] || draft.options[0];

  return (
    <div className="vbo">
      {notification.message && (
        <div className={`vbo__notice vbo__notice--${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="vbo__layout">
        {/* Sidebar: existing sets */}
        <aside className="vbo__sidebar">
          <div className="vbo__sidebar-header">
            <h3>Value Based Sets</h3>
            <button className="vbo__btn vbo__btn--primary" onClick={handleNew}>
              + New
            </button>
          </div>
          {sets.length === 0 ? (
            <p className="vbo__empty">No sets yet — create your first one.</p>
          ) : (
            <ul className="vbo__set-list">
              {sets.map((s) => (
                <li
                  key={s.id}
                  className={`vbo__set-item ${editingId === s.id ? "is-active" : ""}`}
                >
                  <button
                    className="vbo__set-item-main"
                    onClick={() => handleEdit(s)}
                    title="Edit set"
                  >
                    <span className="vbo__set-item-title">
                      {s.title || "(untitled)"}
                    </span>
                    <span className="vbo__set-item-meta">
                      {(s.options || []).length} option
                      {(s.options || []).length === 1 ? "" : "s"}
                    </span>
                  </button>
                  <div className="vbo__set-item-actions">
                    <button
                      className="vbo__icon-btn"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadJson(s);
                      }}
                    >
                      ⬇ JSON
                    </button>
                    <button
                      className="vbo__icon-btn vbo__icon-btn--danger"
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(s.id);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Editor */}
        <section className="vbo__editor">
          <div className="vbo__editor-header">
            <div className="vbo__editor-titles">
              <h2>{editingId ? "Edit Value-Based Offer Set" : "New Value-Based Offer Set"}</h2>
              <p className="vbo__editor-subtitle">
                Define a base set with up to {MAX_OPTIONS} options. Each option contains
                chapters, each chapter contains modules with text and optional bullet
                points. Stays JSON-friendly for export.
              </p>
            </div>
            <div className="vbo__editor-actions">
              {editingId && (
                <button
                  className="vbo__btn vbo__btn--ghost"
                  onClick={() => {
                    const current = sets.find((s) => s.id === editingId);
                    if (current) downloadJson(current);
                  }}
                >
                  ⬇ Download JSON
                </button>
              )}
              <button className="vbo__btn vbo__btn--primary" onClick={handleSave}>
                {editingId ? "Save Changes" : "Save Set"}
              </button>
            </div>
          </div>

          <div className="vbo__form-grid">
            <div className="vbo__field">
              <label>Set Title</label>
              <input
                type="text"
                className="vbo__input"
                placeholder="e.g. Outreach Engine — Value Tiers"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </div>
            <div className="vbo__field">
              <label>Currency</label>
              <select
                className="vbo__input"
                value={draft.currency}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, currency: e.target.value }))
                }
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
            <div className="vbo__field vbo__field--full">
              <label>Description (optional)</label>
              <textarea
                className="vbo__input vbo__textarea"
                placeholder="Short context for this set"
                value={draft.description}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, description: e.target.value }))
                }
              />
            </div>
          </div>

          {/* Option tabs */}
          <div className="vbo__option-tabs">
            {draft.options.map((opt, i) => (
              <button
                key={opt.id}
                className={`vbo__option-tab ${
                  activeOptionIdx === i ? "is-active" : ""
                }`}
                onClick={() => setActiveOptionIdx(i)}
              >
                <span className="vbo__option-tab-title">
                  {opt.title || `Option ${i + 1}`}
                </span>
                <span className="vbo__option-tab-price">
                  {Number(opt.price) || 0} {draft.currency}
                  {Number(opt.setupFee) > 0 && (
                    <span className="vbo__option-tab-setup">
                      {" "}
                      + {Number(opt.setupFee)} Setup
                    </span>
                  )}
                </span>
              </button>
            ))}
            {draft.options.length < MAX_OPTIONS && (
              <button className="vbo__option-tab vbo__option-tab--add" onClick={addOption}>
                + Option
              </button>
            )}
          </div>

          {activeOption && (
            <div className="vbo__option-panel">
              <div className="vbo__option-head">
                <div className="vbo__field">
                  <label>Option Title</label>
                  <input
                    type="text"
                    className="vbo__input"
                    value={activeOption.title}
                    onChange={(e) =>
                      updateOption(activeOptionIdx, { title: e.target.value })
                    }
                  />
                </div>
                <div className="vbo__field vbo__field--narrow">
                  <label>Price ({draft.currency})</label>
                  <input
                    type="number"
                    min="0"
                    className="vbo__input"
                    value={activeOption.price}
                    onChange={(e) =>
                      updateOption(activeOptionIdx, {
                        price: Number(e.target.value) || 0,
                      })
                    }
                  />
                </div>
                <div className="vbo__field vbo__field--narrow">
                  <label>
                    Setup Fee ({draft.currency})
                    <span className="vbo__label-hint"> · optional, einmalig</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="vbo__input"
                    placeholder="0"
                    value={activeOption.setupFee || 0}
                    onChange={(e) =>
                      updateOption(activeOptionIdx, {
                        setupFee: Math.max(0, Number(e.target.value) || 0),
                      })
                    }
                  />
                </div>
                {draft.options.length > 1 && (
                  <button
                    className="vbo__btn vbo__btn--danger-soft"
                    onClick={() => removeOption(activeOptionIdx)}
                  >
                    Remove Option
                  </button>
                )}
              </div>

              {/* Chapters */}
              <div className="vbo__chapters">
                {activeOption.chapters.map((chp, chpIdx) => (
                  <div className="vbo__chapter" key={chp.id}>
                    <div className="vbo__chapter-head">
                      <input
                        type="text"
                        className="vbo__input vbo__input--lg"
                        placeholder={`Chapter ${chpIdx + 1} title`}
                        value={chp.title}
                        onChange={(e) =>
                          updateChapter(activeOptionIdx, chpIdx, {
                            title: e.target.value,
                          })
                        }
                      />
                      <div className="vbo__chapter-actions">
                        <button
                          className="vbo__icon-btn"
                          onClick={() => moveChapter(activeOptionIdx, chpIdx, -1)}
                          disabled={chpIdx === 0}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          className="vbo__icon-btn"
                          onClick={() => moveChapter(activeOptionIdx, chpIdx, 1)}
                          disabled={chpIdx === activeOption.chapters.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          className="vbo__icon-btn vbo__icon-btn--danger"
                          onClick={() => removeChapter(activeOptionIdx, chpIdx)}
                          title="Delete chapter"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    <div className="vbo__modules">
                      {chp.modules.map((mod, modIdx) => (
                        <div className="vbo__module" key={mod.id}>
                          <div className="vbo__module-head">
                            <input
                              type="text"
                              className="vbo__input"
                              placeholder={`Module ${modIdx + 1} title`}
                              value={mod.title}
                              onChange={(e) =>
                                updateModule(activeOptionIdx, chpIdx, modIdx, {
                                  title: e.target.value,
                                })
                              }
                            />
                            <div className="vbo__module-actions">
                              <button
                                className="vbo__icon-btn"
                                onClick={() =>
                                  moveModule(activeOptionIdx, chpIdx, modIdx, -1)
                                }
                                disabled={modIdx === 0}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                className="vbo__icon-btn"
                                onClick={() =>
                                  moveModule(activeOptionIdx, chpIdx, modIdx, 1)
                                }
                                disabled={modIdx === chp.modules.length - 1}
                                title="Move down"
                              >
                                ↓
                              </button>
                              <button
                                className="vbo__icon-btn vbo__icon-btn--danger"
                                onClick={() =>
                                  removeModule(activeOptionIdx, chpIdx, modIdx)
                                }
                                title="Delete module"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          <textarea
                            className="vbo__input vbo__textarea vbo__textarea--sm"
                            placeholder="Module text (optional)"
                            value={mod.text}
                            onChange={(e) =>
                              updateModule(activeOptionIdx, chpIdx, modIdx, {
                                text: e.target.value,
                              })
                            }
                          />
                          <div className="vbo__bullets">
                            {mod.bullets.map((b, bIdx) => (
                              <div className="vbo__bullet-row" key={bIdx}>
                                <span className="vbo__bullet-dot">•</span>
                                <input
                                  type="text"
                                  className="vbo__input vbo__input--sm"
                                  placeholder="Bullet point"
                                  value={b}
                                  onChange={(e) =>
                                    updateBullet(
                                      activeOptionIdx,
                                      chpIdx,
                                      modIdx,
                                      bIdx,
                                      e.target.value
                                    )
                                  }
                                />
                                <button
                                  className="vbo__icon-btn vbo__icon-btn--danger"
                                  onClick={() =>
                                    removeBullet(
                                      activeOptionIdx,
                                      chpIdx,
                                      modIdx,
                                      bIdx
                                    )
                                  }
                                  title="Remove bullet"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            <button
                              className="vbo__btn vbo__btn--ghost vbo__btn--sm"
                              onClick={() =>
                                addBullet(activeOptionIdx, chpIdx, modIdx)
                              }
                            >
                              + Add Bullet
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        className="vbo__btn vbo__btn--ghost"
                        onClick={() => addModule(activeOptionIdx, chpIdx)}
                      >
                        + Add Module
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  className="vbo__btn vbo__btn--secondary"
                  onClick={() => addChapter(activeOptionIdx)}
                >
                  + Add Chapter
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ValueBasedOffers;
